/**
 * SessionAssignmentTab — Assignment tab content for SessionDetailsModal.
 * Displays station/operator/packer assignments, weight modes, split entries
 * list, and weight validation. (Source Location now lives in the Details tab.)
 */
import React from 'react';
import { Layers, Plus } from 'lucide-react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { SubSession } from '../../../../services/productionService';
import { Operator, StationWithDetails } from '../../../../services/stationManagementService';
import StatusDropdown from '../../../../components/StatusDropdown';
import { StationSplitEntry } from './useSessionSplitting';

interface SessionAssignmentTabProps {
  session: any;
  subSessions: SubSession[];
  canEdit: boolean;
  canSplit: boolean | undefined;
  isReadOnly: boolean;
  isSplitting: boolean;
  stationSplitEntries: StationSplitEntry[];
  availableStations: StationWithDetails[];
  sortedOperators: Operator[];
  availableOperators: Operator[];
  availablePackers: Operator[];
  efficiencyMap: Map<string, any>;
  efficiencySortBy: string;
  setEfficiencySortBy: (s: any) => void;
  totalAllocatedWeight: number;
  isSplitValid: boolean | string;
  entriesBelowMinWeight: StationSplitEntry[];
  canEditSubSession: (ss: SubSession) => boolean;
  updateStationEntry: (index: number, field: string, value: string | number | null) => void;
  openSubdivideModal: (subSessionNumber: string) => void;
  setShowAddMaterialModal: (v: boolean) => void;
  setPendingStationChange: (v: any) => void;
  setShowStationChangeModal: (v: boolean) => void;
  onSubSessionStatusChange: (subSessionNumber: string, newStatus: string) => void;
}

const SessionAssignmentTab: React.FC<SessionAssignmentTabProps> = ({
  session, subSessions, canEdit, canSplit, isReadOnly, isSplitting,
  stationSplitEntries, availableStations, sortedOperators, availableOperators, availablePackers,
  efficiencyMap, efficiencySortBy, setEfficiencySortBy,
  totalAllocatedWeight, isSplitValid, entriesBelowMinWeight,
  canEditSubSession, updateStationEntry,
  openSubdivideModal, setShowAddMaterialModal,
  setPendingStationChange, setShowStationChangeModal, onSubSessionStatusChange,
}) => {
  const { t } = useLanguage();

  const hasOnHoldMaterials = session.materials && session.materials.some((m: any) => m.lot_approval_status === 'pending');

  return (
    <div className="space-y-6">
      {/* On-hold material alert */}
      {hasOnHoldMaterials && (
        <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
          <span className="text-red-500 mt-0.5">&#9888;</span>
          <div>
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              {t('sessionDetails.materialOnHoldAlert')}
            </p>
            <p className="text-xs text-red-600 dark:text-red-500 mt-0.5">
              {session.materials
                .filter((m: any) => m.lot_approval_status === 'pending')
                .map((m: any) => `${m.item_number} (${m.transfer_number})`)
                .join(', ')
              }
            </p>
          </div>
        </div>
      )}

      {/* Split Session Section */}
      {((canEdit && canSplit) || subSessions.length > 0) && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {subSessions.length > 0
                  ? t('sessionDetails.subSessions')
                  : t('sessionDetails.splitSession')}
              </h3>
            </div>
            {subSessions.length > 0 && !isReadOnly && (
              <button
                onClick={() => setShowAddMaterialModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('sessionDetails.addMaterial') || 'Add Material'}
              </button>
            )}
          </div>

          {(isSplitting || subSessions.length > 0) ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {stationSplitEntries[0]?.material_item_number
                  ? t('sessionDetails.splitByMaterialDescription')
                  : `${t('sessionDetails.splitDescription')} (${session.initial_material_weight ? Number(session.initial_material_weight).toFixed(2) : 0} lbs)`
                }
              </p>

              {stationSplitEntries.map((entry, index) => {
                const matchedSubSession = entry.sub_session_number ? subSessions.find(ss => ss.sub_session_number === entry.sub_session_number) : null;
                const isRowEditable = !matchedSubSession || canEditSubSession(matchedSubSession);
                const rowDisabled = !isRowEditable;
                // WCO-2352 / WCO-2723: an operator can be (re)assigned while the sub-session is
                // pending/submitted/received/ready. `ready` means "eligible to start" but no operator
                // has actually started work yet, so reassigning is a normal supervisor correction.
                // active/paused have real in-progress work (locked); completed/cancelled are terminal.
                const operatorDisabled = rowDisabled || !!(matchedSubSession && !['pending', 'submitted', 'received', 'ready'].includes(matchedSubSession.status));
                // Weight is read-only in every status on this tab (WCO-2376). Station/operator/packer stay editable per status.
                const weightDisabled = true;
                const minWeight = matchedSubSession && ['received', 'ready', 'active', 'paused'].includes(matchedSubSession.status)
                  ? (Number(matchedSubSession.actual_output_weight) || 0) + (Number(matchedSubSession.waste_weight) || 0)
                  : 0;

                return (
                <div key={index} className={`p-3 rounded-lg border relative ${rowDisabled ? 'bg-gray-100 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-80' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600'}`}>
                  {/* Status dropdown (replaces static status pill) */}
                  {matchedSubSession && (
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      <StatusDropdown
                        currentStatus={matchedSubSession.status}
                        subSessionNumber={matchedSubSession.sub_session_number}
                        hasStation={!!matchedSubSession.station_id}
                        hasOperator={!!matchedSubSession.assigned_operator}
                        onStatusChange={onSubSessionStatusChange}
                        size="sm"
                      />
                    </div>
                  )}
                  {/* Material info header for multi-material mode */}
                  {entry.material_item_number && (
                    <div className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                      <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                        {entry.material_item_number}
                      </span>
                      {entry.material_name && entry.material_name !== entry.material_item_number && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          {entry.material_name}
                        </span>
                      )}
                      {entry.material_transfer_number && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                          (Lot: {entry.material_transfer_number})
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-3 items-start">
                    {/* Station */}
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {entry.material_item_number ? t('sessionDetails.station') : `${t('sessionDetails.station')} ${index + 1}`}
                      </label>
                      <select
                        value={entry.station_id || ''}
                        onChange={(e) => {
                          const newStationId = e.target.value ? parseInt(e.target.value) : null;
                          if (matchedSubSession && ['active', 'paused'].includes(matchedSubSession.status) && newStationId && newStationId !== matchedSubSession.station_id) {
                            updateStationEntry(index, 'station_id', newStationId);
                            setPendingStationChange({
                              subSessionNumber: matchedSubSession.sub_session_number,
                              newStationId: newStationId,
                              entryIndex: index
                            });
                            setShowStationChangeModal(true);
                          } else {
                            updateStationEntry(index, 'station_id', newStationId);
                          }
                        }}
                        disabled={rowDisabled}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm ${rowDisabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                      >
                        <option value="">{t('sessionDetails.selectStation')}</option>
                        {entry.station_id && entry.station_name && !availableStations.some(s => s.station_id === entry.station_id) && (
                          <option key={entry.station_id} value={entry.station_id}>
                            {entry.station_name}
                          </option>
                        )}
                        {availableStations.map((station, idx) => (
                          <option key={station.station_id ?? `st-${idx}`} value={station.station_id}>
                            {station.station_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Operator */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                          {t('sessionDetails.operator')}
                        </label>
                        {efficiencyMap.size > 0 && index === 0 && (
                          <div className="flex gap-1 text-[10px]">
                            {(['both', 'yield', 'speed'] as const).map(s => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => setEfficiencySortBy(s)}
                                className={`px-1.5 py-0.5 rounded ${efficiencySortBy === s ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'}`}
                              >
                                {t(`operatorEfficiency.${s}`)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <select
                        value={entry.operator_id || ''}
                        onChange={(e) => updateStationEntry(index, 'operator_id', e.target.value || null)}
                        disabled={operatorDisabled}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm ${operatorDisabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                      >
                        <option value="">{t('sessionDetails.selectOperator')}</option>
                        {(() => {
                          const matchingSS = entry.sub_session_number
                            ? subSessions.find(ss => ss.sub_session_number === entry.sub_session_number)
                            : subSessions.find(ss => ss.station_id === entry.station_id);
                          const operatorName = matchingSS?.operator_name;
                          if (entry.operator_id && operatorName && !sortedOperators.some(o => o.employee_number === entry.operator_id)) {
                            return (
                              <option key={entry.operator_id} value={entry.operator_id}>
                                {operatorName}
                              </option>
                            );
                          }
                          return null;
                        })()}
                        {sortedOperators
                          .filter(o => !stationSplitEntries.some((e, i) => i !== index && e.operator_id === o.employee_number))
                          .map((operator, idx) => {
                            const eff = efficiencyMap.get(operator.employee_number);
                            const label = eff
                              ? `${operator.full_name} — ${t('operatorEfficiency.avgYield')}: ${Number(eff.avg_yield).toFixed(2)}% (#${eff.yield_rank}) | ${eff.avg_speed_lbs_hr} ${t('operatorEfficiency.lbsPerHour')} (#${eff.speed_rank})`
                              : `${operator.full_name} — ${t('operatorEfficiency.noHistory')}`;
                            return (
                              <option key={operator.employee_number ?? `op-${idx}`} value={operator.employee_number}>
                                {label}
                              </option>
                            );
                          })}
                      </select>
                    </div>
                    {/* Packer */}
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('sessionDetails.assignPacker')}
                      </label>
                      <select
                        value={entry.packer_id || ''}
                        onChange={(e) => updateStationEntry(index, 'packer_id', e.target.value || null)}
                        disabled={rowDisabled}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm ${rowDisabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'}`}
                      >
                        <option value="">{t('sessionDetails.selectPacker')}</option>
                        {(() => {
                          const matchingSS = entry.sub_session_number
                            ? subSessions.find(ss => ss.sub_session_number === entry.sub_session_number)
                            : subSessions.find(ss => ss.station_id === entry.station_id);
                          const packerName = matchingSS?.packer_name;
                          if (entry.packer_id && packerName && !availablePackers.some(p => p.employee_number === entry.packer_id)) {
                            return (
                              <option key={entry.packer_id} value={entry.packer_id}>
                                {packerName}
                              </option>
                            );
                          }
                          return null;
                        })()}
                        {availablePackers.map((packer, idx) => (
                          <option key={packer.employee_number ?? `pk-${idx}`} value={packer.employee_number}>
                            {packer.full_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Weight */}
                    <div className="w-28">
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {t('sessionDetails.materialWeight')}
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        min={minWeight}
                        value={entry.material_weight}
                        onChange={(e) => updateStationEntry(index, 'material_weight', e.target.value)}
                        placeholder="0"
                        readOnly={weightDisabled}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 text-sm ${
                          weightDisabled
                            ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed'
                            : 'bg-white dark:bg-gray-700'
                        }`}
                      />
                      {minWeight > 0 && !weightDisabled && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          {t('sessionDetails.alreadyUsed') || 'Used'}: {minWeight.toFixed(2)} lbs
                        </span>
                      )}
                    </div>
                    {/* Subdivide button */}
                    {matchedSubSession && canEditSubSession(matchedSubSession) && (
                      <button
                        onClick={() => openSubdivideModal(matchedSubSession.sub_session_number)}
                        className="mt-6 p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                        title={t('sessionDetails.subdivide') || 'Subdivide'}
                      >
                        <Layers className="w-4 h-4" />
                      </button>
                    )}
                    {(!matchedSubSession || (matchedSubSession && !canEditSubSession(matchedSubSession))) && (
                      <div className="mt-6 w-8 h-8" />
                    )}
                  </div>
                </div>
                );
              })}

              {/* Weight validation (single-material mode only) */}
              {!stationSplitEntries[0]?.material_item_number && !isReadOnly && (
                <>
                  <div className="flex justify-end items-center">
                    <div className="text-sm text-right">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('sessionDetails.totalWeight')}: </span>
                        <span className={`font-medium ${
                          Math.abs(totalAllocatedWeight - (Number(session.initial_material_weight) || 0)) < 0.5
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {totalAllocatedWeight.toFixed(2)} / {session.initial_material_weight ? Number(session.initial_material_weight).toFixed(2) : 0} lbs
                        </span>
                      </div>
                      {Math.abs(totalAllocatedWeight - (Number(session.initial_material_weight) || 0)) >= 0.5 && (
                        <div className="text-amber-600 dark:text-amber-400 font-medium">
                          {t('sessionDetails.remaining') || 'Remaining'}: {((Number(session.initial_material_weight) || 0) - totalAllocatedWeight).toFixed(2)} lbs
                        </div>
                      )}
                    </div>
                  </div>

                  {!isSplitValid && totalAllocatedWeight > 0 && Math.abs(totalAllocatedWeight - (Number(session.initial_material_weight) || 0)) >= 0.5 && (
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {t('sessionDetails.weightMismatch')}
                    </p>
                  )}

                  {entriesBelowMinWeight.length > 0 && (
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      {t('sessionDetails.minWeightWarning')
                        .replace('{{count}}', String(entriesBelowMinWeight.length))
                        .replace('{{minWeight}}', (session.unit_weight! / (session.materials?.[0]?.yield_factor || session.raw_material_yield_factor || 1.0)).toFixed(2))
                      }
                    </p>
                  )}
                </>
              )}

              {/* Total weight summary in multi-material mode */}
              {stationSplitEntries[0]?.material_item_number && (
                <div className="text-sm text-right">
                  <span className="text-gray-500 dark:text-gray-400">{t('sessionDetails.totalWeight')}: </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {totalAllocatedWeight.toFixed(2)} lbs
                  </span>
                </div>
              )}

            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('sessionDetails.splitHint')}
            </p>
          )}
        </div>
      )}

    </div>
  );
};

export default SessionAssignmentTab;
